import * as React from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import tw, { styled } from 'twin.macro';

import { BaseModal, ButtonV2, Title } from '@restworld/ui-ds';
import { AddressFieldType } from '@restworld/data-services';

import { googleAddresInputValueAtom } from './google-address-input-result';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import RestaurantOnboarding from './restaurant-onboarding';
import { RestaurantOnboardingFooter } from './restaurant-onboarding-footer';
import {
  useRestaurantOnboardingFormReset,
  useRestaurantOnboardingResetActions,
} from './restaurant-onboarding-modal.hooks';

export type RestaurantOnboardingStage =
  | 'intro'
  | 'select_restaurant_from_google'
  | 'magic_ai_loading'
  | 'restaurant_onboarding_form'
  | 'restaurant_owner_onboarding_form'
  | 'restaurant_onboarding_successfull'
  | 'restaurant_already_exists'
  | 'contact_already_exists'
  | 'otp_verification'
  | 'not_horeca';

export const restaurantOnboardingFormDefaultValues = {
  restaurant_name: '',
  address: '',
  brief_description: '',
  covers_per_service: '',
  instagram_url: '',
  facebook_url: '',
  website_url: '',
  the_fork_url: '',
  opening_hours: '',
  number_of_staffs: '',
  name: '',
  surname: '',
  email: '',
  phone_number: '',
  privacy_policy: false,
  terms_and_conditions: false,
  vexatious_clauses_acceptance: false,
};
// Atoms
export const activeRestaurantOnboardingStateAtom = atom<RestaurantOnboardingStage>('select_restaurant_from_google');
export const restaurantOnboardingModalVisibleAtom = atom(false);
export const isManualEntryAtom = atom(false);
export const manualAddressFieldsAtom = atom<Partial<AddressFieldType>>({});
// Latest Google Places `status` observed (OK, ZERO_RESULTS, OVER_QUERY_LIMIT, REQUEST_DENIED,
// INVALID_REQUEST, UNKNOWN_ERROR, NOT_FOUND, ...). Empty string means no status seen yet.
// (Using an empty-string sentinel rather than `null` because this tsconfig has
// `strict: false`, which makes `atom<string | null>(null)` resolve to the read-only overload.)
export const googlePlacesLastStatusAtom = atom('');
// Set to true when an `employer_onboarding_google_search_no_result` event was emitted
// during the current restaurant-creation step session. Read at manual form submit to
// fill `had_prior_no_result`.
export const googleSearchNoResultSeenAtom = atom(false);

// Main Component modal for authenticated user
const RestaurantOnboardingModal: React.FunctionComponent = () => {
  const restaurantOnboardingModalVisible = useAtomValue(restaurantOnboardingModalVisibleAtom);
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);

  const isInputFilled = googleInputAddressValue.length > 0;
  const methods = useForm<RestaurantOnboardingPayload>({ defaultValues: restaurantOnboardingFormDefaultValues });

  const { resetState, handleModalClose } = useRestaurantOnboardingResetActions();
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const setManualAddressFields = useSetAtom(manualAddressFieldsAtom);

  const handleBack = React.useCallback(() => {
    if (activeUI === 'restaurant_already_exists' || activeUI === 'restaurant_onboarding_form') {
      setActiveUI('select_restaurant_from_google');
      setIsManualEntry(false);
      setManualAddressFields({});
    } else {
      resetState();
    }
  }, [activeUI, resetState, setActiveUI, setIsManualEntry, setManualAddressFields]);
  const { t: tC } = useTranslation('common');

  useRestaurantOnboardingFormReset({ methods });

  const isRestaurantOnboardingSuccessful = activeUI === 'restaurant_onboarding_successfull';
  const modalProps = React.useMemo(
    () => ({
      open: restaurantOnboardingModalVisible,
      onClose: handleModalClose,
      showBackButton: activeUI === 'restaurant_onboarding_form' || activeUI === 'restaurant_already_exists',
      onBack: handleBack,
      backIcon: (
        <ButtonV2 variant="tertiary" StartIcon={ChevronLeftIcon} tw="shadow-none">
          <Title content={tC('back')} />
        </ButtonV2>
      ),
      headerStyles: !isRestaurantOnboardingSuccessful && [tw`border-b border-neutral-40`],
      modalStyles: [
        tw`h-full pb-0 max-h-[36rem] md:(max-h-[36rem]) xl:max-h-[40rem] 2xl:max-h-[50rem] overflow-x-hidden`,
        isRestaurantOnboardingSuccessful && tw`bg-purple-50`,
      ],
      footer: <RestaurantOnboardingFooter methods={methods} />,
      footerStyles: [
        activeUI === 'magic_ai_loading' ||
        activeUI.includes('already_exists') ||
        activeUI === 'restaurant_onboarding_successfull' ||
        (activeUI === 'select_restaurant_from_google' && !isInputFilled)
          ? tw`border-none hidden`
          : tw`border-t border-neutral-40 py-4`,
      ],
      noPadding: isRestaurantOnboardingSuccessful,
      hideHeader: isRestaurantOnboardingSuccessful,
    }),
    [
      activeUI,
      handleBack,
      handleModalClose,
      isInputFilled,
      isRestaurantOnboardingSuccessful,
      methods,
      restaurantOnboardingModalVisible,
      tC,
    ]
  );

  return (
    <BaseModal {...modalProps}>
      <RestaurantOnboardingWrapperStyled>
        <RestaurantOnboarding methods={{ ...methods }} />
      </RestaurantOnboardingWrapperStyled>
    </BaseModal>
  );
};

export default RestaurantOnboardingModal;
const RestaurantOnboardingWrapperStyled = styled.div`
  ${tw`w-full md:(min-w-[32rem] max-w-[32rem]) lg:(min-w-[48rem] max-w-[48rem]) xl:(min-w-[64rem] max-w-[64rem]) pb-0`}
  ${tw`h-full max-h-[36rem] md:(max-h-[36rem]) xl:max-h-[40rem] 2xl:max-h-[50rem]`}
`;
